/**
 * The deposit and hold lifecycle (B4, tv-9jp4): one pure module the web mirror,
 * the server validators and the intents all read, so a deposit's state, its
 * slot, its sender and its plan rules are decided in ONE place.
 *
 *   - `slotBookingId` — the ONE slot key: the held booking, else the booking the
 *     link points at (a part-paid / firm link carries only `bookingId`) (7w8f).
 *   - `isLinkEffectivelyExpired` — a link is expired from its pay-by instant, not
 *     from the next sweep, on every screen and on the public pay route (7g3y, 7kv4).
 *   - `isLegalDepositTransition` — the stored-status graph a client write must
 *     follow. `forfeited -> paid` is only ever the server's reinstate intent (75ng).
 *   - `depositPhase` / `linkSenderRole` / `linkSenderPersonId` — one projection every screen reads (7kv4, 8tqu).
 *   - `MIN_TOPUP_INSTALMENT_AUD` / `spreadInstalments` / `planScheduleProblem` —
 *     the top-up plan rules: every instalment >= $50, all due by the appointment (75my).
 *   - `piecePotAud` — the one deposit pot a piece carries across its sessions (75mu).
 */
import { ledgerPaidAud, type CancelSplitLedgerEntry } from './cancelSplit';
import { isLiveBooking } from './bookingStates';
import { slotBookingId, type SlotKeyDeposit } from './slotKey';

// ── The slot key (./slotKey) ────────────────────────────────────────────────

export { slotBookingId, type SlotKeyDeposit };

// ── Money in, expiry ────────────────────────────────────────────────────────

export interface LifecycleDeposit extends SlotKeyDeposit {
  status: string;
  amountAud: number;
  amountPaid?: number | null;
  expiresAt: string;
  ledger?: readonly CancelSplitLedgerEntry[];
  payAtStudio?: boolean | null;
  supersededByDepositId?: string | null;
  sentByRole?: string | null;
  /** The person who sent the link (W5); older links record only takenByAgentId. */
  sentBy?: string | null;
  takenByAgentId?: string | null;
  source?: string | null;
}

/** Money on a deposit: the larger of its ledger net and amount_paid. */
export function moneyInAud(d: Pick<LifecycleDeposit, 'ledger' | 'amountPaid' | 'status' | 'amountAud'>): number {
  const amountPaid = d.amountPaid == null ? undefined : Number(d.amountPaid);
  const net = ledgerPaidAud({
    ...(d.ledger ? { ledger: [...d.ledger] } : {}),
    ...(amountPaid !== undefined ? { amountPaid } : {}),
    status: d.status,
    amountAud: Number(d.amountAud),
  });
  return Math.max(net, amountPaid ?? 0);
}

/**
 * Has this LINK expired at `nowIso`? Only an unpaid link can: still `pending`,
 * not a pay-at-studio hold (never on the link clock), no money on it, and past
 * its pay-by. A stored `expired` is expired whatever the clock says. This is the
 * moment the customer page stops offering pay, the public pay route answers 410,
 * and the ledger and Ops both read "Expired" — the sweep that later releases the
 * slot is housekeeping, not the decision.
 */
export function isLinkEffectivelyExpired(d: LifecycleDeposit, nowIso: string): boolean {
  if (d.status === 'expired') return moneyInAud(d) <= 0;
  if (d.status !== 'pending') return false;
  if (d.payAtStudio) return false;
  if (moneyInAud(d) > 0) return false;
  const exp = Date.parse(d.expiresAt);
  return Number.isFinite(exp) && exp <= Date.parse(nowIso);
}

// ── The stored-status graph ─────────────────────────────────────────────────

/** Stored deposit statuses (the DB CHECK). */
export const DEPOSIT_STATUSES = ['pending', 'paid', 'expired', 'forfeited', 'refunded', 'rolled-over'] as const;

const LEGAL_DEPOSIT_NEXT: Readonly<Record<string, readonly string[]>> = {
  pending: ['paid', 'expired', 'forfeited'],
  paid: ['forfeited', 'refunded', 'rolled-over'],
  // A reinstate moves a forfeit back to paid, but only through the server's
  // reinstate intent (a server-internal write), never a client PUT.
  forfeited: [],
  expired: [],
  refunded: [],
  'rolled-over': [],
};

/**
 * May a CLIENT write move a deposit from `from` to `to`? Staying put is always
 * fine. `serverInternal` (an intent / sweep, no actor) may also reinstate.
 */
export function isLegalDepositTransition(from: string, to: string, opts: { serverInternal?: boolean } = {}): boolean {
  if (from === to) return true;
  if (opts.serverInternal && from === 'forfeited' && to === 'paid') return true;
  return (LEGAL_DEPOSIT_NEXT[from] ?? []).includes(to);
}

/** Ledger kinds only the server writes (reinstate / forfeit carry authority and a reason). */
export const SERVER_ONLY_LEDGER_KINDS: ReadonlySet<string> = new Set(['reinstate']);

// ── The sender ──────────────────────────────────────────────────────────────

export type LinkSenderRole = 'agent' | 'messenger' | 'sweeper' | 'self-book';

/**
 * Who sent a link. The recorded `sentByRole` wins; older links fall back: a
 * gap-fill link or a confirmed-on-call hold is the Sweeper's; an artist /
 * self-book link is customer-driven; anything else is the Agent's (the desk and
 * queue paths). The ledger and Ops both read this (7kv4).
 */
export function linkSenderRole(
  d: Pick<LifecycleDeposit, 'sentByRole' | 'source' | 'heldBookingId' | 'bookingId'>,
  bookings: Readonly<Record<string, { confirmedOnCall?: boolean | null } | undefined>> = {},
): LinkSenderRole {
  if (d.sentByRole === 'agent' || d.sentByRole === 'messenger' || d.sentByRole === 'sweeper' || d.sentByRole === 'self-book') {
    return d.sentByRole;
  }
  if (d.source === 'gap-fill') return 'sweeper';
  const slot = slotBookingId(d);
  if (slot && bookings[slot]?.confirmedOnCall) return 'sweeper';
  if (d.source === 'artist-link' || d.source === 'self-book-link') return 'self-book';
  return 'agent';
}

/**
 * WHO (the person) sent a link: the recorded `sentBy`, else the agent who took
 * it (`takenByAgentId`, all an older or seeded link records). Undefined when no
 * person is known; then screens show the role (`linkSenderRole`). The ledger and
 * Ops both read this, so a named agent reads by name on both (tv-7n5f, 7kv4).
 */
export function linkSenderPersonId(d: Pick<LifecycleDeposit, 'sentBy' | 'takenByAgentId'>): string | undefined {
  return d.sentBy || d.takenByAgentId || undefined;
}

// ── The phase projection ────────────────────────────────────────────────────

export type DepositPhase =
  | 'link-open'
  | 'link-expired'
  | 'pay-at-studio'
  | 'part-paid'
  | 'on-file'
  | 'placed'
  | 'lapsed'
  | 'forfeited'
  | 'superseded'
  | 'used'
  | 'rolled-over'
  | 'refunded';

export interface PhaseBooking {
  status: string;
}

/** True while the latest forfeit movement is unreversed by a later reinstate. */
export function hasUnreversedForfeit(d: Pick<LifecycleDeposit, 'ledger'>): boolean {
  let lastForfeit = -1;
  let lastReinstate = -1;
  (d.ledger ?? []).forEach((e, i) => {
    if (e.kind === 'forfeit') lastForfeit = i;
    if (e.kind === 'reinstate') lastReinstate = i;
  });
  return lastForfeit >= 0 && lastReinstate < lastForfeit;
}

/**
 * Where a deposit is in its life, for every screen (ledger, Ops, Sweeper, Floor
 * chip, customer page). `bookings` resolves the slot for the lapsed / placed reads.
 */
export function depositPhase(
  d: LifecycleDeposit,
  ctx: { nowIso: string; bookings?: Readonly<Record<string, PhaseBooking | undefined>> },
): DepositPhase {
  if (d.status === 'rolled-over') return 'rolled-over';
  if (d.status === 'refunded') return 'refunded';
  if (d.status === 'forfeited' || hasUnreversedForfeit(d)) return 'forfeited';
  if ((d.ledger ?? []).some((e) => e.kind === 'redeemed')) return 'used';
  if (d.supersededByDepositId) return 'superseded';
  const money = moneyInAud(d);
  if (d.status === 'pending' && d.payAtStudio && money <= 0) return 'pay-at-studio';
  if (isLinkEffectivelyExpired(d, ctx.nowIso)) return 'link-expired';
  const slot = slotBookingId(d);
  const booking = slot ? ctx.bookings?.[slot] : undefined;
  if (d.status === 'paid' && booking?.status === 'no-show') return 'lapsed';
  if (money > 0 && money < Number(d.amountAud)) return 'part-paid';
  if (d.status === 'paid' && !slot) return 'on-file';
  if (d.status === 'paid') return booking && !isLiveBooking(booking) ? 'on-file' : 'placed';
  return 'link-open';
}

// ── Top-up plan rules ───────────────────────────────────────────────────────

/** Every instalment is at least this (75my); a whole plan under it is one instalment. */
export const MIN_TOPUP_INSTALMENT_AUD = 50;

const DAY_MS = 86_400_000;

export interface PlanInstalment {
  dueIso: string;
  amountAud: number;
  status?: string;
}

/** The calendar day (YYYY-MM-DD, Australia/Melbourne) of an instant. */
function melbourneDay(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Melbourne', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(iso),
  );
}

/**
 * Split `totalAud` into instalments of `instalmentAud` every week / fortnight
 * from `startIso`. A remainder under the $50 floor is folded into the instalment
 * before it (so $125 at $50 is $50 + $75, never $50 + $50 + $25). With an
 * `appointmentIso`, a schedule that would run past the appointment day is
 * re-spread over the instalments that fit, so the last one is due by then.
 */
export function spreadInstalments(opts: {
  totalAud: number;
  instalmentAud: number;
  frequency: 'weekly' | 'fortnightly';
  startIso: string;
  appointmentIso?: string;
}): PlanInstalment[] {
  const total = Math.max(0, Math.round(opts.totalAud));
  if (total <= 0) return [];
  const step = (opts.frequency === 'fortnightly' ? 14 : 7) * DAY_MS;
  const per = Math.max(MIN_TOPUP_INSTALMENT_AUD, Math.round(opts.instalmentAud));
  // At the chosen amount, with the remainder as the last instalment — folded into
  // the one before when it would be under the floor ($125 at $50 is $50 + $75,
  // never $50 + $50 + $25; $1,000 at $300 stays 3 x $300 + $100).
  let natural = Math.max(1, Math.ceil(total / per));
  if (natural > 1 && total - per * (natural - 1) < MIN_TOPUP_INSTALMENT_AUD) natural -= 1;
  let count = natural;
  if (opts.appointmentIso) {
    const lastDay = melbourneDay(opts.appointmentIso);
    let fit = 0;
    while (fit < natural && melbourneDay(addStudioDays(opts.startIso, fit * (step / DAY_MS))) <= lastDay) fit += 1;
    count = Math.max(1, Math.min(count, fit));
  }
  const amounts: number[] = [];
  if (count === 1) amounts.push(total);
  else if (count === natural) {
    for (let i = 0; i < count; i += 1) amounts.push(per);
    amounts[count - 1] = total - per * (count - 1);
  } else {
    // Fewer instalments fit before the appointment: spread the total evenly
    // over them (each is then above the chosen amount).
    const base = Math.floor(total / count);
    for (let i = 0; i < count; i += 1) amounts.push(base);
    amounts[count - 1] = total - base * (count - 1);
  }
  return amounts.map((amountAud, i) => ({ dueIso: addStudioDays(opts.startIso, i * (step / DAY_MS)), amountAud }));
}

/**
 * `iso` moved on by whole studio (Melbourne) days, keeping its wall-clock time
 * across a daylight-saving change — 7 x 24h after the April change would land
 * on the previous evening.
 */
export function addStudioDays(iso: string, days: number): string {
  const startMs = Date.parse(iso);
  const [y, m, d] = melbourneDay(iso).split('-').map(Number) as [number, number, number];
  const want = new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
  let t = startMs + days * DAY_MS;
  for (const shift of [0, 3_600_000, -3_600_000]) {
    if (melbourneDay(new Date(t + shift).toISOString()) === want) {
      t += shift;
      break;
    }
  }
  return new Date(t).toISOString();
}

export type PlanScheduleProblem = 'instalment-under-minimum' | 'instalment-after-appointment';

/**
 * What's wrong with a plan's schedule, or undefined when it keeps the rules:
 * every unpaid instalment is at least $50 (a plan whose whole total is under $50
 * is one instalment), and none is due after the appointment day. Paid
 * instalments are history and never judged.
 */
export function planScheduleProblem(
  instalments: readonly PlanInstalment[],
  opts: { totalAud: number; appointmentIso?: string },
): PlanScheduleProblem | undefined {
  const open = instalments.filter((i) => i.status !== 'paid' && i.status !== 'cancelled');
  const small = Number(opts.totalAud) < MIN_TOPUP_INSTALMENT_AUD && instalments.length === 1;
  if (!small && open.some((i) => Number(i.amountAud) < MIN_TOPUP_INSTALMENT_AUD)) return 'instalment-under-minimum';
  if (opts.appointmentIso) {
    const lastDay = melbourneDay(opts.appointmentIso);
    if (open.some((i) => melbourneDay(i.dueIso) > lastDay)) return 'instalment-after-appointment';
  }
  return undefined;
}

/** A plan's schedule in words, from the real amounts: "$50 + $75", "3 × $200". */
export function scheduleLabel(instalments: readonly PlanInstalment[]): string {
  if (instalments.length === 0) return '';
  const amounts = instalments.map((i) => Math.round(i.amountAud));
  const fmt = (n: number) => `$${n.toLocaleString('en-AU')}`;
  if (amounts.every((a) => a === amounts[0])) return `${amounts.length} × ${fmt(amounts[0]!)}`;
  const head = amounts.slice(0, -1);
  const last = amounts[amounts.length - 1]!;
  if (head.every((a) => a === head[0]) && head.length > 1) return `${head.length} × ${fmt(head[0]!)} + ${fmt(last)}`;
  return amounts.map(fmt).join(' + ');
}

// ── The piece pot ───────────────────────────────────────────────────────────

/**
 * The money a piece's deposit chain holds right now (75mu, diagram 01: the
 * deposit "belongs to the piece"). Each session has its own deposit row, linked
 * by rollovers; the pot is their net: money in (paid, top-ups, reinstates) less
 * what was forfeited, refunded or used up. A rollover moves money between rows
 * of the same pot, so it nets to zero across the chain.
 */
export function piecePotAud(deposits: readonly LifecycleDeposit[]): number {
  let pot = 0;
  for (const d of deposits) {
    if (d.status === 'refunded' || d.status === 'forfeited' || d.status === 'expired') continue;
    // Its money moved to the next session's deposit, which carries it; seeded and
    // older chains never wrote the rollover-out line, so don't rely on the net.
    if (d.status === 'rolled-over') continue;
    if (hasUnreversedForfeit(d)) continue;
    if ((d.ledger ?? []).some((e) => e.kind === 'redeemed')) continue;
    pot += moneyInAud(d);
  }
  return Math.max(0, pot);
}

// ── An outside-7-day top-up date move (75my, diagram 02; Anas 1 Oct Q4) ─────

/** How long the moved date is held as a ghost while the partial is unpaid (the link window). */
export const MOVE_PARTIAL_WINDOW_HOURS = 24;
/** The reason stamped on the OLD booking when a move's partial isn't paid in time (Anas, Q4: the old date goes too). */
export const MOVE_LAPSED_REASON = 'Top-up date move lapsed: the partial was not paid';
/** The reason stamped on the move's held new date when it is released. */
export const MOVE_GHOST_RELEASED_REASON = 'Top-up date move: new date released';

// ── An overbooked this-week slot lost to another client (D02-07 / D05-19) ────

/** `deposits.released_reason` on a losing hold's link: another client paid first. */
export const SLOT_TAKEN_RELEASE = 'slot-taken' as const;
/** The reason stamped on the losing hold (the cancelled ghost booking). */
export const SLOT_TAKEN_CANCEL_REASON = 'Another client paid for this time first';

export interface MoveSchedule {
  /** Taken now to secure the new date (>= $50, or the whole balance when that is less). */
  partialAud: number;
  /** The plan's instalments after the move: paid history, the partial due now, the rest spread to the new date. */
  instalments: PlanInstalment[];
}

/**
 * The plan after an outside-7-day move: its paid instalments stay as history,
 * a partial of at least $50 (at least the instalment that was due) is due now,
 * and the rest is re-spread weekly / fortnightly to finish on or before the NEW
 * appointment, each at least $50. Undefined when nothing is owed.
 */
export function planMoveSchedule(opts: {
  totalAud: number;
  instalments: readonly PlanInstalment[];
  frequency: 'weekly' | 'fortnightly';
  nowIso: string;
  newStartIso: string;
}): MoveSchedule | undefined {
  const paid = opts.instalments.filter((i) => i.status === 'paid');
  const paidAud = paid.reduce((a, i) => a + Number(i.amountAud), 0);
  const owed = Math.round(Number(opts.totalAud) - paidAud);
  if (owed <= 0) return undefined;
  const due = opts.instalments.find((i) => i.status !== 'paid' && i.status !== 'cancelled');
  const usual = Math.max(MIN_TOPUP_INSTALMENT_AUD, Math.round(Number(due?.amountAud ?? MIN_TOPUP_INSTALMENT_AUD)));
  let partialAud = Math.min(owed, usual);
  let rest = owed - partialAud;
  if (rest > 0 && rest < MIN_TOPUP_INSTALMENT_AUD) {
    partialAud += rest;
    rest = 0;
  }
  const step = (opts.frequency === 'fortnightly' ? 14 : 7) * DAY_MS;
  const restPlan =
    rest > 0
      ? spreadInstalments({
          totalAud: rest,
          instalmentAud: usual,
          frequency: opts.frequency,
          startIso: addStudioDays(opts.nowIso, step / DAY_MS),
          appointmentIso: opts.newStartIso,
        })
      : [];
  return {
    partialAud,
    instalments: [
      ...paid,
      { dueIso: opts.nowIso, amountAud: partialAud, status: 'scheduled' },
      ...restPlan.map((i) => ({ ...i, status: 'scheduled' })),
    ],
  };
}

/** When a move's ghost lapses if its partial is still unpaid. */
export function moveLapsesAt(ghostCreatedAtIso: string): string {
  return new Date(Date.parse(ghostCreatedAtIso) + MOVE_PARTIAL_WINDOW_HOURS * 3_600_000).toISOString();
}

// ── Holds and self-book links (B5) ──────────────────────────────────────────

const HOLD_HOURS = 24;
const THIS_WEEK_GRACE_HOURS = 2;

/** Is `slotIso` in the current Mon–Sun studio week of `nowIso` (from today on)? */
export function isThisStudioWeek(slotIso: string, nowIso: string): boolean {
  const today = melbourneDay(nowIso);
  const slotDay = melbourneDay(slotIso);
  if (slotDay < today) return false;
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 Sun .. 6 Sat
  const daysLeft = dow === 0 ? 1 : 8 - dow;
  const nextMonday = new Date(Date.UTC(y, m - 1, d + daysLeft)).toISOString().slice(0, 10);
  return slotDay < nextMonday;
}

/**
 * The system customer a pinned self-book slot waits under until a client pays
 * (B5). A server row only (the hold's foreign keys need it); never a client on
 * any screen, count or picker, so the views and bootstrap leave it out.
 */
export const AWAITING_CLIENT_ID = 'awaiting-client';

/**
 * When a held slot's link lapses (diagram 02; Anas 1 Oct B5 Q2): 24 hours, plus
 * a 2-hour grace for a slot this week. The same rule as the web's deposit links
 * (depositWindow.holdExpiresAtIso), for the server's holds.
 */
export function holdLapsesAt(slotIso: string, nowIso: string): string {
  const hours = HOLD_HOURS + (isThisStudioWeek(slotIso, nowIso) ? THIS_WEEK_GRACE_HOURS : 0);
  return new Date(Date.parse(nowIso) + hours * 3_600_000).toISOString();
}

export interface SelfBookLinkLike {
  status: string;
  universal?: boolean | null;
  customerId?: string | null;
  expiresAt: string;
}

/**
 * Does a named (staff-sent) self-book link expire at `nowIso` (B5, 8xar)? Only an
 * unpaid, unexpired link to a named client: a universal link is reusable and has
 * no client to follow up. The same `<=` boundary as a deposit link.
 */
export function isSelfBookLinkExpirable(link: SelfBookLinkLike, nowIso: string): boolean {
  if (link.status !== 'active' && link.status !== 'viewed') return false;
  if (link.universal || !link.customerId) return false;
  const exp = Date.parse(link.expiresAt);
  return Number.isFinite(exp) && exp <= Date.parse(nowIso);
}

// ── "Already booked" (B7) ───────────────────────────────────────────────────

/** Booking statuses that hold no slot any more, or are done with it. */
const NOT_UPCOMING: ReadonlySet<string> = new Set(['cancelled', 'no-show', 'superseded', 'completed']);
/** Statuses a lapsed link frees (a firm booking is not on the link clock). */
const LINK_HELD: ReadonlySet<string> = new Set(['held', 'pending-deposit']);

export interface UpcomingSlotBooking {
  id: string;
  status: string;
  start: string;
  durationMin: number;
}

/**
 * Is this booking a live slot or hold its client still has, from `nowIso` on
 * (B7, 75nh D08-14; Anas 1 Oct Q1: "already booked" = any live booking or hold
 * with ANY artist)? Not cancelled / no-show / superseded / completed, not over
 * yet, and not a hold whose link has lapsed — `isLinkEffectivelyExpired` is the
 * moment a hold stops being one (B4), whether or not the sweep has run.
 * `deposits` are the client's deposits; only one keyed to this slot matters.
 */
export function isLiveUpcomingSlot(
  b: UpcomingSlotBooking,
  deposits: readonly LifecycleDeposit[],
  nowIso: string,
): boolean {
  if (NOT_UPCOMING.has(b.status)) return false;
  const end = Date.parse(b.start) + b.durationMin * 60_000;
  if (!(end > Date.parse(nowIso))) return false;
  if (LINK_HELD.has(b.status)) {
    const lapsed = deposits.some((d) => slotBookingId(d) === b.id && isLinkEffectivelyExpired(d, nowIso));
    if (lapsed) return false;
  }
  return true;
}
